import { ServiceAreas, slugifyAreaName } from './service-areas';
import type { ComponentConfig } from '@puckeditor/core';
import type { JobsiteBusinessContext } from "../../types/runtime-config";
import { RegenerableTextField } from "../editor/RegenerableTextField";
import { ServiceAreasFields } from "./service-areas.fields";
import type { ServiceAreasProps } from './service-areas';

const pathExists = (knownPaths: string[], path: string): boolean => knownPaths.some((p) => p === path || p === `${path}/`),

  /**
   * The page this area should link to, chosen from the paths the site actually
   * has: the service×location combo under `baseUrl` when one was generated,
   * otherwise the plain location page. Returns undefined rather than guessing,
   * so a business with no location pages renders plain chips instead of 404s.
   */
  resolveAreaPagePath = (
    slug: string,
    baseUrl: string | undefined,
    knownPaths: string[] | undefined,
  ): string | undefined => {
    if (!slug) {
      return undefined;
    }

    const comboPath = baseUrl ? `${baseUrl.replace(/\/$/u, "")}/${slug}` : undefined,
      locationPath = `/locations/${slug}`;

    // The editor preview ships no page list; keep its existing baseUrl behaviour
    // Rather than inventing a link the build would not produce.
    if (!knownPaths) {
      return comboPath;
    }

    if (comboPath && pathExists(knownPaths, comboPath)) {
      return comboPath;
    }
    if (pathExists(knownPaths, locationPath)) {
      return locationPath;
    }
    return undefined;
  },

  ServiceAreasConfig: ComponentConfig<ServiceAreasProps> = {
    defaultProps: {
      areas: [
        { displayName: "Austin, TX", name: "austin" },
        { displayName: "Round Rock, TX", name: "round-rock" },
        { displayName: "Cedar Park, TX", name: "cedar-park" },
        { displayName: "Pflugerville, TX", name: "pflugerville" },
        { displayName: "Georgetown, TX", name: "georgetown" },
        { displayName: "Leander, TX", name: "leander" },
      ],
      baseUrl: "/locations/",
      heading: "Areas We Serve",
      layout: "grid",
      showDescriptions: false,
      showLinks: true,
      showMap: true,
      subheading:
        "Proudly serving homeowners throughout the greater metro area and surrounding communities.",
    },
    fields: {
      heading: {
        ...ServiceAreasFields.heading,
        contentEditable: true,
        render: ({ value, onChange, field }) => (
          <RegenerableTextField
            value={value || ""}
            onChange={onChange}
            namespace="serviceAreas.heading"
            label="Heading"
            multiline={false}
            fieldMeta={field?.metadata}
          />
        ),
      },
      subheading: {
        ...ServiceAreasFields.subheading,
        contentEditable: true,
        render: ({ value, onChange, field }) => (
          <RegenerableTextField
            value={value || ""}
            onChange={onChange}
            namespace="serviceAreas.subheading"
            label="Subheading"
            multiline={true}
            fieldMeta={field?.metadata}
          />
        ),
      },
      layout: ServiceAreasFields.layout,
      variant: ServiceAreasFields.variant,
      showLinks: ServiceAreasFields.showLinks,
      showDescriptions: ServiceAreasFields.showDescriptions,
      showMap: ServiceAreasFields.showMap,
      baseUrl: ServiceAreasFields.baseUrl,
      areas: ServiceAreasFields.areas as any,
      backgroundColor: ServiceAreasFields.backgroundColor,
    },
    label: "Service Areas",
    render: ({ id, puck, editMode: _editMode, ...props }) => {
      // Business data flows through puck.metadata.business (NEVER read window globals here)
      const metadata = puck?.metadata as
          | {
              business?: JobsiteBusinessContext;
              site?: { googleMapsApiKey?: string };
              pagePaths?: string[];
            }
          | undefined,
        business = metadata?.business,
        metaServiceAreas = business?.serviceAreas,
        googleMapsApiKey = metadata?.site?.googleMapsApiKey,

        // Only use baseUrl if combo pages actually exist — prevents dead links on sites
        // That have service areas but no service_location pages generated yet.
        knownPaths = metadata?.pagePaths,
        resolvedBaseUrl =
          props.baseUrl && knownPaths
            ? (knownPaths.some((p) => p.startsWith(props.baseUrl!))
              ? props.baseUrl
              : undefined)
            : props.baseUrl,

        // Prefer metadata service areas over stored props (they reflect live business data)
        resolvedAreas =
          metaServiceAreas && metaServiceAreas.length > 0
            ? metaServiceAreas.map((sa) => ({
                boundary: sa.boundary ?? null,
                displayName: sa.displayName ?? sa.name ?? "",
                latitude: sa.latitude ?? null,
                longitude: sa.longitude ?? null,
                name: sa.name ?? "",
                slug: sa.slug,
                surroundingAreas: sa.surroundingAreas ?? false,
                // The business payload carries no URL, so the area's own page is
                // Matched out of the site's real paths. Without this the metadata
                // Areas render unlinked and every location page is orphaned.
                url: resolveAreaPagePath(sa.slug ?? slugifyAreaName(sa.name ?? ""), props.baseUrl, knownPaths),
              }))
            : props.areas;

      return (
        <ServiceAreas
          id={id}
          {...props}
          baseUrl={resolvedBaseUrl}
          areas={resolvedAreas}
          googleMapsApiKey={googleMapsApiKey}
        />
      );
    },
    /**
     * Live business service areas replace the hand-typed list entirely, so on any site that has
     * them the array below is edited and then thrown away. Compact never renders descriptions.
     */
    resolveFields: (data, { fields, metadata }) => {
      const businessAreas = (metadata as { business?: { serviceAreas?: unknown[] } } | undefined)
          ?.business?.serviceAreas,
        visible = { ...(fields as Record<string, unknown>) };

      if (Array.isArray(businessAreas) && businessAreas.length > 0) {
        delete visible.areas;
      }

      if ((data.props as { layout?: string })?.layout === "compact") {
        delete visible.showDescriptions;
      }

      return visible as typeof fields;
    },
  };

export { ServiceAreasConfig };
